import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SheetShell from './SheetShell';
import type { GlossaryEntry } from '@/lib/glossary';
import styles from './GlossarySheet.module.css';

interface GlossarySheetProps {
  entry: GlossaryEntry;
  onClose: () => void;
  /** Coordinator z for the `glossary` NavStack layer (AB, rule #78). */
  zIndex?: number;
}

/**
 * Bottom-sheet definition surface for Bible reader Study Mode.
 * Mounted by BibleChapter when the user taps a glowing word. Reuses
 * the shared SheetShell (backdrop + slide-up + iOS keyboard math —
 * the last is unused here but free) and renders a short, contemplative
 * card with the headword + plain-meaning gloss. Optional small tag
 * surfaces when the entry came from the locations master.
 *
 * Escape key dismisses; backdrop tap dismisses (SheetShell handles).
 * Drag-to-dismiss is enabled here only (no draft, no data-loss risk).
 * No explicit X button — the backdrop tap zone IS the close affordance,
 * matching iOS contemplative-sheet patterns (Apple Books Look Up).
 */
export default function GlossarySheet({ entry, onClose, zIndex }: GlossarySheetProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <SheetShell
      ariaLabel={t('glossary.sheetAria', { headword: entry.headword })}
      onBackdrop={onClose}
      closeLabel={t('nav.close')}
      zIndex={zIndex}
      dragToDismiss
    >
      <div className={styles.body}>
        <div className={styles.headwordRow} data-sheet-drag>
          <h2 className={styles.headword}>{entry.headword}</h2>
          {entry.source === 'location' && (
            <span className={styles.locationTag}>
              {t('glossary.locationTag')}
            </span>
          )}
        </div>
        <p className={styles.definition}>{entry.definition}</p>
      </div>
    </SheetShell>
  );
}
